import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { Link } from '#components/common/Link';
import { cartao, cor, texto } from '#components/finapp/estilo';

type Props = {
  /** Já pago neste mês, em centavos, positivo. */
  pago: number;
  /** Ainda a pagar neste mês, em centavos, positivo. */
  aPagar: number;
  /**
   * ⚠️⚠️ Fix round 1 (Critical) — sem estes dois campos, uma falha em
   * `useAgendadosDoMes` (consulta de `schedules` fora do ar, por exemplo)
   * fazia `contasDoMes` virar `[]` e `pago`/`aPagar` chegarem aqui como
   * `0`/`0` — indistinguível de "nenhum compromisso cadastrado". Ver o
   * JSDoc completo em `Inicio.tsx`, onde `agendadosErro`/`agendadosCarregando`
   * são calculados.
   */
  carregando?: boolean;
  erro?: Error | null;
};

/**
 * Uma linha e uma barra — substitui a lista completa que `ContasDoMes.tsx`
 * mostrava aqui. A lista não sumiu: mudou de endereço, para `/compromissos`.
 * A tela Mês é para bater o olho; a lista é para trabalhar.
 *
 * ⚠️ Divergência do brief: `Link` vem de `#components/common/Link` (como
 * `ajustes/GrupoDeAtalhos.tsx` já usa), não de `@actual-app/components/link`
 * — esse módulo não existe no component-library (conferido antes de editar).
 *
 * ⚠️⚠️ Fix round 1 (Critical) — mesma ORDEM de estados que `ContasDoMes.tsx`
 * já usa (Fix round 1, Finding 3 daquele componente): `erro` vence tudo
 * (nunca mostra "R$0,00 de R$0,00" ao lado de uma falha — isso pareceria um
 * mês sem compromisso nenhum), depois `carregando`, e só então os números de
 * verdade. Sem essa ordem, o número principal da tela (`resumoExibido.livre`,
 * no Herói) mente para o lado OTIMISTA sempre que a consulta de agendados
 * falhar — ver o aviso `heroi-fixas-incertas` em `Inicio.tsx`, que cobre a
 * outra metade do mesmo risco.
 */
export function ResumoCompromissos({
  pago,
  aPagar,
  carregando = false,
  erro = null,
}: Props) {
  const { t } = useTranslation();
  const total = pago + aPagar;
  const fracao = total === 0 ? 0 : pago / total;

  return (
    <View style={{ ...cartao, gap: 10 }} data-testid="resumo-compromissos">
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Text style={texto.rotuloCartao}>
          <Trans>Compromissos fixos</Trans>
        </Text>
        <Link variant="internal" to="/compromissos" style={texto.rotuloTabela}>
          {t('ver todos')}
        </Link>
      </View>

      {erro ? (
        <Text
          data-testid="resumo-compromissos-erro"
          style={{ ...texto.corpo, color: cor.saida }}
        >
          {t('Não consegui ler seus compromissos: {{erro}}', {
            erro: erro.message,
          })}
        </Text>
      ) : carregando ? (
        <Text data-testid="resumo-compromissos-carregando" style={texto.corpo}>
          <Trans>Carregando…</Trans>
        </Text>
      ) : (
        <>
          <Text style={texto.valorCartao}>
            {t('{{pago}} de {{total}}', {
              pago: integerToCurrency(pago),
              total: integerToCurrency(total),
            })}
          </Text>

          <View
            style={{
              height: 8,
              borderRadius: 4,
              backgroundColor: cor.neutroFraco,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                width: `${Math.round(fracao * 100)}%`,
                height: '100%',
                backgroundColor: cor.acento,
              }}
              data-testid="compromissos-barra"
            />
          </View>

          {aPagar > 0 && (
            <>
              <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
                {t('faltam {{valor}} para fechar o mês', {
                  valor: integerToCurrency(aPagar),
                })}
              </Text>

              {/*
               * ⚠️⚠️ Revisão final da branch (Finding 2, Important) — a
               * frase que reconciliava "Entrou − Gastei − Guardei" com o
               * Herói morava em `inicio/ContasDoMes.tsx`
               * (`data-testid="contas-aviso"`), que saiu da montagem de
               * `Inicio.tsx` na Task 9 (painel-panorama). O Herói já
               * desconta `fixasPrevistas` do "Livre pra decidir"; as três
               * colunas de "Entrou · Gastei · Guardei" NÃO incluem
               * `fixasPrevistas` em "Gastei" (só o que já saiu de fato) —
               * então somar as três colunas de cabeça dá um valor MAIOR que
               * o Herói, de propósito, e sem esta frase ninguém explica por
               * quê. Movida pra cá (o mesmo lugar que já mostra `aPagar`) em
               * vez de reviver `ContasDoMes`.
               */}
              <Text
                data-testid="resumo-compromissos-aviso"
                style={{ ...texto.corpo, color: cor.tintaFraca }}
              >
                {t(
                  '{{valor}} em contas fixas ainda não pagas já está descontado do "Livre pra decidir" acima — e não entra em "Gastei" até você pagar.',
                  { valor: integerToCurrency(aPagar) },
                )}
              </Text>
            </>
          )}
        </>
      )}
    </View>
  );
}
